import { getDb } from "./firebaseConfig.js";
import { ref, set, child, push, get, update, runTransaction, onValue } from "firebase/database";

const numBoards = 864;

window.ref = (...args) => ref(...args);
window.set = (...args) => set(...args);
window.child = (...args) => child(...args);
window.push = (...args) => push(...args);
window.db = () => getDb();

const db = () => getDb();

export function createRateLimitedSender(sendFn, maxHz = 20) {
  const interval = 1000 / maxHz;
  let latestData = null;
  let timerId = null;
  let lastSent = 0;

  function flush() {
    timerId = null;

    if (latestData === null) return;

    lastSent = performance.now();
    sendFn(latestData);
    latestData = null;
  }

  return function requestSend(data) {
    latestData = data; // coalesce

    if (timerId !== null) return;

    const now = performance.now();
    const delay = Math.max(0, interval - (now - lastSent));

    timerId = setTimeout(flush, delay);
  };
}

export async function createUser(userId) {
  const userRef = child(ref(db()), "users/"+userId);
  await set(userRef, {
    matchId: null
  });
}

/**
 * Matches are created using the userId. This prevents a user from creating a
 * bunch of matches and never deleting the old ones. Might change this later.
 */
export async function createMatch(userId, boardId) {
  const matchId = userId;
  const matchRef = child(ref(db()), "matches/"+matchId);

  await set(matchRef, {
    players: { player1: userId, player2: null },
    createdAt: Date.now(),
    boardId
  });

  const userRef = ref(db(), "users/"+userId);
  await set(userRef, { matchId });

  return matchId;
}

/**
 *  Called only by player1! Callback is called when player2 joins the match.
 */
export async function onReadyPlayer1(userId, matchId, callback) {
  const matchRef = ref(db(), `matches/${matchId}`);
  const player2Ref = child(matchRef, "players/player2");

  await update(matchRef, { players: { player1: userId } });

  return onValue(player2Ref, (snapshot) => {
    if (snapshot.val() != null) {
      callback();
    }
  });
}

/**
 *  Note: by joining match, you are definitionally player2
 */
export async function joinMatch(userId, matchId) {
  const userRef = ref(db(), "users/" + userId);
  const matchRef = ref(db(), `matches/${matchId}`);

  const matchData = (await get(matchRef)).val();

  // Errors handled, now time to add player2
  const player2Ref = ref(db(), `matches/${matchId}/players/player2`);
  let player2Id;

  await runTransaction(player2Ref, currentData => {
    if (currentData == null || currentData == userId ) {
      return userId;
    } else {
      return currentData;
    }
  }).then((e)=>{
    if (e.committed) player2Id = e.snapshot.val();
  }).catch((e)=>{
    return Promise.reject(e);
  });

  if (player2Id != userId) {
    console.log("This match is already taken by another player!");
    return Promise.reject("matchError");
  }

  // Assuming all went well, it's safe to set this.
  await set(userRef, { matchId });

  // TODO: set up listeners for player1 selection.

  const boardId = matchData.boardId;
  const boardRef = ref(db(), "boards/"+boardId);
  const boardSnapshot = await get(boardRef);
  if (boardSnapshot.exists()) {
    return Promise.resolve(boardSnapshot.val());
  } else {
    return Promise.reject("Board does not exist");
  }
}

/**
 *  Called only by player2 when player1 is ready
 */
export function onReadyPlayer2(matchId) {
  // TODO: this.
  // const player1Id = matchData.players.player1;
  // if (player1Id == null) return Promise.reject("matchError");
  // if (player1Id == userId) return Promise.reject("stupidUserError");
}


/**
 *  Returns a randomly generated board and a list of words that can be spelled
 *  from it. There are numBoards number of boards that are keyed as e.g. "0012".
 */
export async function getRandomBoard() {
  const boardRef = ref(db(), 'boards');
  const i = Math.floor(Math.random()*numBoards);
  const boardId = String(i).padStart(4, '0');

  const snapshot = await get(child(boardRef, boardId));
  if (!snapshot.exists()) {
    throw new Error("Board not found");
    return { board: "XXXXXXXXXXXXXXXX", words: [], boardId };
  }
  
  return { ...snapshot.val(), boardId };
}

export async function foundWord(client, word) {
  const wordsRef = ref(db(), `matches/${client.matchId}/foundWords/${client.player}`);
  await set(push(wordsRef), word);
}

export const updateSelection = createRateLimitedSender(
  (v) => {
    const selectionRef = ref(db(), `matches/${v.matchId}/selections/${v.player}`);
    return set(selectionRef, { indices: v.indices });
  },
  20
);

